/**
 * usePersistedState - A hook that persists state to localStorage
 *
 * Use this for reporting view state that should survive navigation and page refreshes.
 */

import { useState, useCallback } from "react";

export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored) as T;
    } catch {
      return defaultValue;
    }
  });

  const setPersistedState = useCallback(
    (value: T) => {
      setState(value);
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        console.warn(`Failed to persist state for key: ${key}`);
      }
    },
    [key]
  );

  return [state, setPersistedState];
}
